import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";

/**
 * Permanent public Privacy Policy page.
 *
 * Renders the SAME translated content the footer PolicyModal renders — every
 * word, every section, every locale (EN/PT/ES) — but as a routable page at
 * /privacy-policy so search engines, direct links and share URLs resolve
 * cleanly and never fall through to the 404 handler.
 *
 * Typography, colours, header, footer, navigation, spacing and responsive
 * behaviour come from the existing site Layout (this component only owns the
 * article body). Nothing here rewrites or shortens the legal text — the
 * i18n `privacyPolicy` object is the single source of truth.
 */
const PrivacyPolicyPage = () => {
  const { t } = useTranslation();

  useEffect(() => { window.scrollTo(0, 0); }, []);

  const sections = t("privacyPolicy.sections", { returnObjects: true }) || [];

  return (
    <main
      data-testid="privacy-policy-page"
      className="max-w-3xl mx-auto px-6 md:px-8 py-16 md:py-24"
    >
      <span className="text-[10px] font-sans tracking-[0.3em] uppercase text-[var(--accent-bronze)] font-semibold block mb-5">
        {t("privacyPolicy.eyebrow")}
      </span>
      <h1
        data-testid="privacy-policy-title"
        className="font-serif text-3xl md:text-4xl font-light text-primary-custom mb-4 leading-tight"
      >
        {t("privacyPolicy.title")}
      </h1>
      <p className="text-[11px] text-secondary-custom uppercase tracking-[0.18em] mb-10">
        {t("privacyPolicy.updated")}
      </p>

      <div className="space-y-9">
        {sections.map((s, i) => (
          <section key={i} data-testid={`privacy-policy-section-${i}`}>
            <h2 className="font-serif text-lg md:text-xl font-normal text-primary-custom mb-2">
              {s.heading}
            </h2>
            <p className="font-serif text-sm md:text-base text-secondary-custom leading-relaxed font-light whitespace-pre-line">
              {s.body}
            </p>
          </section>
        ))}
      </div>
    </main>
  );
};

export default PrivacyPolicyPage;
